import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState, useEffect } from 'react';
import { PlayIcon } from '@components/common/Icon';
import { cn } from '@utils/cn';
export function VideoPlayer({ src, poster, autoPlay = false, className }) {
    const videoRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(autoPlay);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    useEffect(() => {
        const video = videoRef.current;
        if (!video)
            return;
        const handleTimeUpdate = () => setCurrentTime(video.currentTime);
        const handleDurationChange = () => setDuration(video.duration);
        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);
        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('durationchange', handleDurationChange);
        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);
        return () => {
            video.removeEventListener('timeupdate', handleTimeUpdate);
            video.removeEventListener('durationchange', handleDurationChange);
            video.removeEventListener('play', handlePlay);
            video.removeEventListener('pause', handlePause);
        };
    }, []);
    const togglePlay = () => {
        const video = videoRef.current;
        if (!video)
            return;
        if (isPlaying) {
            video.pause();
        }
        else {
            video.play();
        }
    };
    const handleSeek = (e) => {
        const video = videoRef.current;
        if (!video)
            return;
        const time = parseFloat(e.target.value);
        video.currentTime = time;
        setCurrentTime(time);
    };
    const handleVolumeChange = (e) => {
        const video = videoRef.current;
        if (!video)
            return;
        const vol = parseFloat(e.target.value);
        video.volume = vol;
        setVolume(vol);
        setIsMuted(vol === 0);
    };
    const toggleMute = () => {
        const video = videoRef.current;
        if (!video)
            return;
        video.muted = !isMuted;
        setIsMuted(!isMuted);
    };
    const toggleFullscreen = () => {
        const video = videoRef.current;
        if (!video)
            return;
        if (!isFullscreen) {
            if (video.requestFullscreen) {
                video.requestFullscreen();
            }
        }
        else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            }
        }
        setIsFullscreen(!isFullscreen);
    };
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };
    return (_jsxs("div", { className: cn('group relative overflow-hidden rounded-lg bg-black', className), children: [_jsx("video", { ref: videoRef, src: src, poster: poster, autoPlay: autoPlay, className: "h-full w-full", onClick: togglePlay }), !isPlaying && (_jsx("div", { className: "absolute inset-0 flex cursor-pointer items-center justify-center bg-black/30", onClick: togglePlay, children: _jsx("div", { className: "flex h-16 w-16 items-center justify-center rounded-full bg-white/90 transition-transform hover:scale-110", children: _jsx(PlayIcon, { size: "lg", className: "ml-1 text-primary-500" }) }) })), _jsxs("div", { className: "absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 opacity-0 transition-opacity group-hover:opacity-100", children: [_jsx("input", { type: "range", min: "0", max: duration || 0, value: currentTime, onChange: handleSeek, className: "mb-2 w-full" }), _jsxs("div", { className: "flex items-center justify-between text-white", children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsx("button", { onClick: togglePlay, className: "hover:text-primary-400", children: isPlaying ? '⏸' : '▶' }), _jsxs("span", { className: "text-sm", children: [formatTime(currentTime), " / ", formatTime(duration)] })] }), _jsxs("div", { className: "flex items-center gap-3", children: [_jsx("button", { onClick: toggleMute, className: "hover:text-primary-400", children: isMuted ? '🔇' : '🔊' }), _jsx("input", { type: "range", min: "0", max: "1", step: "0.1", value: isMuted ? 0 : volume, onChange: handleVolumeChange, className: "w-20" }), _jsx("button", { onClick: toggleFullscreen, className: "hover:text-primary-400", children: isFullscreen ? '⛶' : '⛶' })] })] })] })] }));
}
